@let workers = (workerOptions$ | async) ?? [];
@let maintenanceTypeOptions = (maintenanceTypeOptions$ | async) ?? [];
@let warrantyOptions = (warrantyOptions$ | async) ?? [];
@let errorOptions = (errorOptions$ | async) ?? [];
@let isWorkersLoading = workersLoadingStatus() === LoadingState.LOADING;
@let isMaintenanceTypesLoading = maintenanceTypeOptionsLoadingStatus() === LoadingState.LOADING;
@let isWarrantyLoading = warrantyOptionsLoadingStatus() === LoadingState.LOADING;
@let isErrorLoading = errorOptionsLoadingStatus() === LoadingState.LOADING;
@let isCreateRequestLoading = createRequestLoadingStatus() === LoadingState.LOADING;

<h1 class="maintenance-request-modal__title">
  {{ 'monitoring.maintenance.modal.requestCreation' | translate }}
</h1>

<form
  class="request-modal"
  [formGroup]="form"
  (ngSubmit)="onSubmit()"
>
  @if (equipmentData; as equipment) {
    <div class="request-modal__equipment">
      <div class="request-modal__equipment-serial">CH: {{ equipment.serialNumber || '-' }}</div>
      <h2 class="request-modal__equipment-title">{{ equipment.model || '-' }}</h2>

      <div class="request-modal__equipment-status">
        <div
          [class]="
            equipment.status
              ? 'status-chip ' + getEquipmentStatusChipClassName(equipment.status)
              : 'status-chip'
          "
        >
          @if (equipment.status) {
            <lib-svg-icon
              [icon]="getEquipmentStatusIcon(equipment.status)"
              size="18"
            />
            {{ getEquipmentStatusTranslationKey(equipment.status) | translate }}

            @let uptime = equipment.uptime;
            @if (shouldDisplayEquipmentUptime(uptime, equipment.status, equipment.mode)) {
              <mon-uptime-timer [initialSeconds]="equipment.correctedUptime ?? uptime" />
            }
          } @else {
            -
          }
        </div>
      </div>

      <p class="request-modal__equipment-line">
        {{ equipment.location?.facilityName ?? '-' }} /
        {{ 'monitoring.devices.device-card.container' | translate }}
        {{ equipment.location?.containerName ?? '-' }} /
        {{ 'monitoring.devices.device-card.rack' | translate }}
        {{ equipment.location?.rackNumber ?? '-' }} /
        {{ 'monitoring.devices.device-card.shelf' | translate }}
        {{ equipment.location?.shelfNumber ?? '-' }} /
        {{ 'monitoring.devices.device-card.place' | translate }}
        {{ equipment.location?.placeNumber ?? '-' }}
      </p>

      <p class="request-modal__equipment-line">
        {{ 'monitoring.devices.device-card.firmware' | translate }}:
        {{ getFirmwareDisplay(equipment) }}
      </p>

      <p class="request-modal__equipment-line request-modal__equipment-network">
        IP: {{ equipment.ipAddress || '-' }}
        <span>MAC: {{ equipment.macAddress | macAddress }}</span>
      </p>
    </div>
  } @else {
    <p class="request-modal__section-title">
      {{ 'monitoring.maintenance.modal.deviceSection' | translate }}
    </p>

    <div class="request-modal__row request-modal__row--4">
      @if (canReadFacilities()) {
        <mon-facility-select
          labelTranslateKey="monitoring.maintenance.modal.facility"
          formControlName="facilityId"
          [fullWidth]="true"
        />
      }
      <lib-input
        type="number"
        [label]="'monitoring.maintenance.modal.containerNumber' | translate"
        formControlName="containerNumber"
      />
      <lib-input
        type="number"
        [label]="'monitoring.maintenance.modal.rack' | translate"
        formControlName="rack"
      />
      <lib-input
        type="number"
        [label]="'monitoring.maintenance.modal.place' | translate"
        formControlName="place"
      />
    </div>

    <div class="request-modal__row request-modal__row--4">
      @if (canReadEquipmentModels()) {
        <mon-equipment-model-select
          labelTranslateKey="monitoring.maintenance.modal.model"
          formControlName="modelId"
          [fullWidth]="true"
        />
      }
      <lib-input
        [label]="'monitoring.maintenance.modal.serialNumber' | translate"
        formControlName="serialNumber"
      />
      <mon-client-select
        formControlName="clientId"
        [fullWidth]="true"
      />
      @if (canReadWorkers()) {
        <lib-select
          [labelTranslateKey]="'monitoring.maintenance.modal.worker'"
          [options]="workers"
          formControlName="workerId"
          [libSkeleton]="isWorkersLoading"
          [fullWidth]="true"
        />
      }
    </div>
  }

  <p class="request-modal__section-title">
    {{ 'monitoring.maintenance.modal.repairSection' | translate }}
  </p>

  <div class="request-modal__row request-modal__row--4">
    <lib-select
      [labelTranslateKey]="'monitoring.maintenance.modal.repairType'"
      [options]="maintenanceTypeOptions"
      formControlName="repairType"
      [libSkeleton]="isMaintenanceTypesLoading"
      [fullWidth]="true"
    />
    <mon-service-center-select
      labelTranslateKey="monitoring.maintenance.modal.serviceCenter"
      [controlEntity]="form.controls.serviceCenterId"
      [fullWidth]="true"
    />
    <lib-select
      [labelTranslateKey]="'monitoring.maintenance.modal.warranty'"
      [options]="warrantyOptions"
      formControlName="warrantyType"
      [libSkeleton]="isWarrantyLoading"
      [fullWidth]="true"
    />
    <lib-select
      [labelTranslateKey]="'monitoring.maintenance.modal.error'"
      [options]="errorOptions"
      formControlName="error"
      [libSkeleton]="isErrorLoading"
      [fullWidth]="true"
    />
  </div>

  <lib-textarea
    class="request-modal__textarea"
    formControlName="description"
    [label]="'monitoring.maintenance.modal.note' | translate"
  />

  <div class="request-modal__actions">
    <lib-button
      class="request-modal__submit"
      [disabled]="isInvalid || isCreateRequestLoading"
      [libSkeleton]="isCreateRequestLoading"
      buttonSize="large"
      buttonType="submit"
    >
      {{ 'monitoring.maintenance.modal.createRequest' | translate }}
    </lib-button>
  </div>
</form>
